import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEmbeddedService } from '../hooks/useEmbeddedService';
import { useEmbeddingContext } from '../hooks/useEmbeddingContext';
import { DEFAULT_IFRAME_SANDBOX, type IframeWrapperProps } from '../../../types/embedding';
import { DEFAULT_TOKEN_REFRESH_CONFIG } from '@/features/auth/services/keycloak';
import { useAuthStore } from '@/features/auth/store';
import { useShellI18n } from '@/features/language/useI18n';
import { EMBEDDED_APP_MESSAGE_SOURCE, isEmbeddingMessage } from '../utils/messaging';
import { buildShellUrlForEmbeddedNavigation } from '../utils/routes';

const INITIAL_CONTEXT_RETRY_DELAYS_MS = [150, 500, 1500];

export const IframeWrapper: React.FC<IframeWrapperProps> = ({
  service,
  className,
  onLoad,
  onError,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const initialContextRetryTimeoutsRef = useRef<number[]>([]);
  const authRefreshInFlightRef = useRef(false);
  const contextData = useEmbeddingContext();
  const { t } = useShellI18n();
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    isLoading,
    error,
    sendMessage,
    onLoad: handleLoad,
    onError: handleError,
  } = useEmbeddedService(service);

  const isMessageFromIframe = useCallback((event: MessageEvent) => {
    const iframeWindow = iframeRef.current?.contentWindow;
    return Boolean(iframeWindow && event.source === iframeWindow);
  }, []);

  const serviceOrigin = new URL(service.url, window.location.href).origin;

  const isMessageForService = useCallback((event: MessageEvent, message: { payload?: unknown }) => {
    if (isMessageFromIframe(event) && event.origin === serviceOrigin) {
      return true;
    }

    const payload = message.payload;
    const appId = payload && typeof payload === 'object'
      ? (payload as { appId?: unknown }).appId
      : null;

    return event.source !== window && event.origin === serviceOrigin && appId === service.id;
  }, [isMessageFromIframe, service.id, serviceOrigin]);

  const sendContextToIframe = useCallback(async (tokenOverride?: string | null) => {
    if (!iframeRef.current) {
      return;
    }

    sendMessage(iframeRef.current, 'theme', contextData.theme, serviceOrigin);
    sendMessage(iframeRef.current, 'language', contextData.language, serviceOrigin);

    if (service.passAuth) {
      const { ensureValidToken, user } = useAuthStore.getState();
      const token = tokenOverride ?? await ensureValidToken(DEFAULT_TOKEN_REFRESH_CONFIG.minValidity);

      sendMessage(iframeRef.current, 'auth', {
        ...contextData.auth,
        token: token ?? undefined,
        user: user
          ? {
              id: user.sub || '',
              name: user.email,
              email: user.email,
              roles: user.roles ?? [],
              permissions: user.permissions ?? [],
            }
          : undefined,
      }, serviceOrigin);
    }

    if (service.passConsent) {
      sendMessage(iframeRef.current, 'consent', contextData.consent, serviceOrigin);
    }
  }, [contextData.auth, contextData.consent, contextData.language, contextData.theme, sendMessage, service.passAuth, service.passConsent, serviceOrigin]);

  const clearInitialContextRetries = useCallback(() => {
    initialContextRetryTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    initialContextRetryTimeoutsRef.current = [];
  }, []);

  const scheduleInitialContextRetries = useCallback(() => {
    clearInitialContextRetries();
    initialContextRetryTimeoutsRef.current = INITIAL_CONTEXT_RETRY_DELAYS_MS.map((delay) =>
      window.setTimeout(() => {
        void sendContextToIframe();
      }, delay)
    );
  }, [clearInitialContextRetries, sendContextToIframe]);

  const handleEmbeddedMessage = useCallback((event: MessageEvent) => {
    if (!isEmbeddingMessage(event.data)) {
      return;
    }

    const message = event.data;
    if (message.source !== EMBEDDED_APP_MESSAGE_SOURCE || !isMessageForService(event, message)) {
      return;
    }

    if (message.type === 'ready') {
      clearInitialContextRetries();
      void sendContextToIframe();
      return;
    }

    if (message.type === 'navigation') {
      const shellUrl = buildShellUrlForEmbeddedNavigation(service, message.payload);
      if (!shellUrl) {
        return;
      }

      const currentUrl = `${location.pathname}${location.search}${location.hash}`;
      if (shellUrl !== currentUrl) {
        const payload = message.payload as { replace?: unknown; shellPath?: unknown };
        // The iframe has already created its own history entry before it
        // reports a local path change. Mirror that entry into the Shell URL
        // with replace so one user navigation remains one joint-history step.
        // A shellPath is instead a command to leave the current app, so it
        // keeps the caller's requested push/replace semantics.
        const replace = typeof payload.shellPath === 'string'
          ? payload.replace === true
          : true;
        navigate(shellUrl, {
          replace,
          state: { portalEmbeddedNavigationServiceId: service.id },
        });
      }
      return;
    }

    if (message.type === 'auth:expired') {
      if (authRefreshInFlightRef.current) {
        return;
      }

      authRefreshInFlightRef.current = true;
      void useAuthStore.getState().ensureValidToken(-1)
        .then((token) => token ? sendContextToIframe(token) : undefined)
        .finally(() => {
          authRefreshInFlightRef.current = false;
        });
      return;
    }

    if (message.type === 'error') {
      console.warn(`Embedded service "${service.id}" reported an auth handoff error`, message.payload);
      return;
    }

    if (message.type === 'custom' && message.payload && typeof message.payload === 'object') {
      const payload = message.payload as { event?: unknown };
      if (payload.event === 'shell-auth-established') {
        clearInitialContextRetries();
        return;
      }

      if (payload.event === 'auth-refresh-required') {
        if (authRefreshInFlightRef.current) {
          return;
        }

        authRefreshInFlightRef.current = true;
        void useAuthStore.getState().ensureValidToken(-1)
          .then((token) => token ? sendContextToIframe(token) : undefined)
          .finally(() => {
            authRefreshInFlightRef.current = false;
          });
      }
    }
  }, [clearInitialContextRetries, isMessageForService, location.hash, location.pathname, location.search, navigate, sendContextToIframe, service]);

  // Combine local and store callbacks
  const handleIframeLoad = () => {
    clearInitialContextRetries();
    handleLoad();
    onLoad?.();

    void sendContextToIframe();
    scheduleInitialContextRetries();
  };

  const handleIframeError = () => {
    const error = new Error(`Failed to load iframe: ${service.url}`);
    handleError(error);
    onError?.(error);
  };

  // Update context when it changes
  useEffect(() => {
    if (iframeRef.current && !isLoading) {
      void sendContextToIframe();
    }
  }, [isLoading, sendContextToIframe]);

  useEffect(() => {
    window.addEventListener('message', handleEmbeddedMessage);
    return () => window.removeEventListener('message', handleEmbeddedMessage);
  }, [handleEmbeddedMessage]);

  useEffect(() => clearInitialContextRetries, [clearInitialContextRetries]);

  if (error) {
    return (
      <div className="embed-error">
        <h3>{t('Failed to load {{name}}', { name: service.name })}</h3>
        <button
          onClick={() => iframeRef.current?.contentWindow?.location.reload()}
          className="btn ghost sm"
          type="button"
        >
          {t('Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className={`embed-frame-wrap ${className || ''}`}>
      {isLoading && (
        <div className="embed-loading-overlay">
          <div className="embed-spinner" />
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={service.url}
        title={service.name}
        className="embed-iframe"
        sandbox={(service.sandboxOptions ?? DEFAULT_IFRAME_SANDBOX).join(' ')}
        allow={service.allowOptions?.join('; ')}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        style={{
          width: service.styles?.width || '100%',
          height: service.styles?.height || '100%',
          minHeight: service.styles?.minHeight || '500px',
        }}
      />
    </div>
  );
};
